import { proxy, useSnapshot } from "valtio";

/** All possible menu"s a user can go to */
export const MenuIds = {
	AlternativeProducts: "alternative-products",
	BackgroundColor: "backgrounds-color",
	BackgroundPattern: "backgrounds-pattern",
	Backgrounds: "backgrounds",
	/** Menu to select Start Date */
	DateSelection: "date-selection",
	Designs: "designs",
	Events: "events",
	FontFamilies: "font-families",
	Layouts: "layouts",
	Map: "map",
	None: undefined,
	Options: "options",
	Photo: "photo",
	PhotoBrightness: "photo-brightness",
	PhotoContrast: "photo-contrast",
	PhotoEditing: "photo-editing",
	PhotoFilters: "photo-filters",
	PhotoRotate: "photo-rotate",
	PhotoSaturation: "photo-saturation",
	PhotoSelection: "photo-selection",
	PhotoTransform: "photo-transform",
	PhotoZoneDepth: "photo-zone-depth",
	ProductOptions: "product-options",
	Qr: "qr",
	QrAdd: "qr-add",
	QrEdit: "qr-edit",
	Root: "",
	Symbols: "symbols",
	SymbolsList: "symbols-list",
	SymbolZoneDepth: "symbol-zone-depth",
	Text: "text",
	TextHAlignment: "text-h-alignment",
	TextVAlignment: "text-v-alignment",
	TextColor: "text-color",
	TextSize: "text-size",
	TextStyle: "text-style",
	TextValue: "text-value",
	TextZoneAdd: "text-zone-add",
	TextZoneDepth: "text-zone-depth",
} as const;

export type MenuId = (typeof MenuIds)[keyof typeof MenuIds];

interface IMenusState {
	/**
	 * The menu that user activated
	 * Depending on the layout, it can be used to visualize the correct menu (bottom, side, top)
	 */
	menuId: MenuId;
}

interface IMenusActions {
	setMenu(menuId: MenuId): void;
}

interface IMenusStore extends IMenusState {
	actions: IMenusActions;
}

/** Create a function, so unit tests can create a fresh instance */
const createMenusState = () => {
	const state = proxy<IMenusStore>({
		menuId: undefined,
		actions: {} as IMenusActions,
	});

	state.actions = {
		setMenu(menuId: MenuId) {
			state.menuId = menuId;
		},
	};

	return state;
};

// The global menus state used by the application
export const menusState = createMenusState();

// Set state as global variable for debugging
if (process.env.PUBLIC_DEBUG === "true") {
	(globalThis as any)._menusState = menusState;
}

export function useMenus() {
	return {
		...(useSnapshot(menusState) as Readonly<IMenusState>),
		actions: menusState.actions,
	};
}
